/**
 * @fileoverview Product Analytics - Retention Analysis
 * @module features/analytics/retention
 * 
 * @description
 * Track and analyze user retention using cohort analysis,
 * identify churn patterns, and measure user stickiness.
 * 
 * @useCases
 * - Measure week-over-week retention rates
 * - Compare retention across user cohorts
 * - Identify churn risk factors
 * - Track feature adoption impact on retention
 * - Calculate DAU/MAU ratios (stickiness)
 * 
 * @metrics
 * - Day 1, 7, 30 retention rates
 * - Cohort retention curves
 * - Churn rate by segment
 * - User resurrection rate
 * - DAU/WAU/MAU ratios
 * 
 * @chartData
 * - Retention curve: Line chart over time
 * - Cohort matrix: Heatmap by cohort/period
 * - Bar chart: Retention by segment
 * - Trend line: Stickiness over time
 * 
 * @example
 * ```typescript
 * const retention = new Retention(webId, endpoint, apiKey);
 * 
 * // Track user activity
 * await retention.trackActivity({
 *   userId: 'user_123',
 *   action: 'feature_used'
 * });
 * 
 * // Get cohort analysis
 * const cohorts = await retention.getCohortAnalysis({
 *   range: 'last90days',
 *   granularity: 'week'
 * });
 * ```
 */

import { FlowGridTransport } from "../../client/transport";
import { 
  RetentionCohort,
  UserLifecycleStage,
  LifecycleMetrics,
  DateRangeFilter,
  TimeGranularity,
  TimeSeriesData
} from "../../types";

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * Configuration for tracking user activity.
 */
export interface ActivityConfig {
  /** User identifier */
  userId: string;
  /** Activity type/action */
  action?: string;
  /** Session ID */
  sessionId?: string;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * Configuration for cohort analysis.
 */
export interface CohortAnalysisConfig {
  /** Date range for analysis */
  filter?: DateRangeFilter;
  /** Cohort granularity */
  granularity?: TimeGranularity;
  /** Segment by trait */
  segmentBy?: string;
  /** Number of periods to show */
  periods?: number;
}

/**
 * Activity tracking response.
 */
export interface ActivityResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    userId: string;
    tracked: boolean;
    daysSinceFirstSeen: number;
    isReturning: boolean;
  };
  timestamp: string;
}

/**
 * Cohort analysis data for charts.
 */
export interface CohortAnalysisData {
  /** Cohort data */
  cohorts: RetentionCohort[];
  /** Retention matrix for heatmap */
  matrix: Array<{
    cohort: string;
    periods: Array<{
      period: number;
      retained: number;
      retentionRate: number;
    }>;
  }>;
  /** Summary metrics */
  summary: {
    avgDay1Retention: number;
    avgDay7Retention: number;
    avgDay30Retention: number;
    overallRetentionTrend: 'improving' | 'declining' | 'stable';
  };
}

/**
 * User stickiness data.
 */
export interface StickinessData {
  /** DAU - Daily Active Users */
  dau: number;
  /** WAU - Weekly Active Users */
  wau: number;
  /** MAU - Monthly Active Users */
  mau: number;
  /** DAU/MAU ratio (stickiness) */
  dauMauRatio: number;
  /** DAU/WAU ratio */
  dauWauRatio: number;
  /** Stickiness trend over time */
  trendData: TimeSeriesData;
}

/**
 * Churn analysis data.
 */
export interface ChurnAnalysisData {
  /** Current churn rate */
  churnRate: number;
  /** Users at risk of churning */
  atRiskUsers: number;
  /** Users who churned this period */
  churnedUsers: number;
  /** Churn by segment */
  bySegment: Array<{
    segment: string;
    churnRate: number;
    count: number;
  }>;
  /** Common churn factors */
  churnFactors: Array<{
    factor: string;
    correlation: number;
  }>;
  /** Churn trend */
  trendData: TimeSeriesData;
}

// ============================================================================
// RETENTION CLASS
// ============================================================================

/**
 * Retention - Track and analyze user retention.
 * 
 * @extends FlowGridTransport
 * 
 * @description
 * Provides retention analysis capabilities including:
 * - User activity tracking
 * - Cohort-based retention analysis
 * - Stickiness metrics (DAU/MAU)
 * - Churn prediction and analysis
 * - Lifecycle stage tracking
 * 
 * @example
 * ```typescript
 * const retention = new Retention(webId, endpoint, apiKey);
 * 
 * // Track daily user activity
 * await retention.trackActivity({
 *   userId: 'user_123',
 *   action: 'daily_login'
 * });
 * 
 * // Analyze cohort retention
 * const analysis = await retention.getCohortAnalysis({
 *   granularity: 'week',
 *   periods: 12
 * });
 * 
 * // Cohort heatmap data
 * const heatmapData = analysis.matrix;
 * ```
 */
export class Retention extends FlowGridTransport {
  /**
   * Tracks user activity for retention calculation.
   * 
   * @param config - Activity configuration
   * @returns Promise resolving to activity response
   * 
   * @example
   * ```typescript
   * // Track on app open
   * await retention.trackActivity({
   *   userId: 'user_123',
   *   action: 'app_opened'
   * });
   * 
   * // Track specific feature usage
   * await retention.trackActivity({
   *   userId: 'user_123',
   *   action: 'used_export',
   *   properties: { format: 'pdf' }
   * });
   * ```
   */
  async trackActivity(config: ActivityConfig): Promise<ActivityResponse> {
    return await this.init("user_activity", {
      type: "user_activity",
      userId: config.userId,
      action: config.action || "active",
      sessionId: config.sessionId,
      ...config.properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks user lifecycle stage change.
   * 
   * @param userId - User identifier
   * @param stage - New lifecycle stage
   * @param previousStage - Previous stage (optional)
   * @returns Promise resolving to stage change response
   */
  async trackLifecycleChange(
    userId: string,
    stage: UserLifecycleStage,
    previousStage?: UserLifecycleStage
  ): Promise<ActivityResponse> {
    return await this.init("lifecycle_change", {
      type: "lifecycle_change",
      userId,
      stage,
      previousStage,
      timestamp: new Date().toISOString()
    });
  }

}

export default Retention;
